import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../constants';
import StarRating from './StarRating';

const API_URL = API_BASE_URL + '/api';

const ArticleFeedbackForm = ({ article, user, onFeedbackSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkExistingFeedback();
  }, [article?.id, user?.id]);

  const checkExistingFeedback = async () => {
    if (!article?.id || !user?.id) return;
    
    try {
      const response = await fetch(
        `${API_URL}/users/${user.id}/feedback?article_id=${article.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.has_feedback) {
          setExistingFeedback(data.feedback);
          setRating(data.feedback.rating);
          setComment(data.feedback.comment || '');
          setIsAnonymous(data.feedback.is_anonymous);
        }
      }
    } catch (error) {
      console.error('Error checking existing feedback:', error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${API_URL}/articles/${article.id}/feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            rating,
            comment: comment.trim(),
            is_anonymous: isAnonymous
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccess(
          existingFeedback 
            ? 'Your feedback has been updated successfully!' 
            : 'Thank you for your feedback!'
        );
        setExistingFeedback(data.feedback);
        
        if (onFeedbackSubmitted) {
          onFeedbackSubmitted(data.feedback);
        }
      } else {
        setError(data.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="feedback-form">
        <p>Loading feedback form...</p>
      </div>
    );
  }

  return (
    <div className="feedback-form">
      <div className="feedback-header">
        <h3>
          {existingFeedback ? 'Update Your Feedback' : 'Rate This Content'}
        </h3>
        <p>Help others by sharing your thoughts on this content</p>
      </div>

      <form onSubmit={handleSubmit}>
        {error && (
          <div className="message error">
            {error}
          </div>
        )}

        {success && (
          <div className="message success">
            {success}
          </div>
        )}

        <div className="rating-section">
          <label className="rating-label">
            Rate this content:
          </label>
          <div className="rating-container">
            <StarRating 
              rating={rating} 
              setRating={setRating}
              disabled={isSubmitting}
              size="large"
            />
            <span className="rating-text">
              {rating === 0 && 'Select a rating'}
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent'}
            </span>
          </div>
        </div>

        <div className="comment-section">
          <label htmlFor="comment" className="comment-label">
            Comments (optional):
          </label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your thoughts about this content... What did you find most valuable? Any suggestions for improvement?"
            rows={4}
            disabled={isSubmitting}
            maxLength={1000}
          />
          <div className="character-count">
            {comment.length}/1000 characters
          </div>
        </div>

        <div className="privacy-section">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              disabled={isSubmitting}
            />
            Submit feedback anonymously
          </label>
          <p className="privacy-note">
            {isAnonymous 
              ? 'Your feedback will be shown as "Anonymous"' 
              : `Your feedback will be attributed to ${user?.first_name} ${user?.last_name}`
            }
          </p>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-submit"
            disabled={isSubmitting || rating === 0}
          >
            {isSubmitting ? 'Submitting...' : (existingFeedback ? 'Update Feedback' : 'Submit Feedback')}
          </button>
        </div>
      </form>

      <style jsx>{`
        .feedback-form {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 2rem;
          margin: 2rem 0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .feedback-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .feedback-header h3 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .feedback-header p {
          color: #6b7280;
          margin: 0;
        }

        .rating-section {
          margin-bottom: 1.5rem;
        }

        .rating-label {
          display: block;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.75rem;
        }

        .rating-container {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .rating-text {
          font-weight: 500;
          color: #6b7280;
          min-width: 100px;
        }

        .comment-section {
          margin-bottom: 1.5rem;
        }

        .comment-label {
          display: block;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.75rem;
        }

        textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 1rem;
          font-family: inherit;
          resize: vertical;
          min-height: 100px;
          transition: border-color 0.2s;
        }

        textarea:focus {
          outline: none;
          border-color: var(--accent-green);
          box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
        }

        textarea:disabled {
          background-color: #f9fafb;
          color: #6b7280;
          cursor: not-allowed;
        }

        .character-count {
          text-align: right;
          font-size: 0.875rem;
          color: #6b7280;
          margin-top: 0.5rem;
        }

        .privacy-section {
          margin-bottom: 2rem;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
        }

        .checkbox-label input[type="checkbox"] {
          width: auto;
        }

        .privacy-note {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0.5rem 0 0 1.5rem;
          font-style: italic;
        }

        .form-actions {
          text-align: center;
        }

        .btn-submit {
          background: linear-gradient(135deg, var(--accent-green), #45a049);
          color: white;
          border: none;
          padding: 0.75rem 2rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 160px;
        }

        .btn-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(76, 175, 80, 0.3);
        }

        .btn-submit:disabled {
          background: #9ca3af;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .message {
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          text-align: center;
          font-weight: 500;
        }

        .message.success {
          background: #ecfdf5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }

        .message.error {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fca5a5;
        }

        @media (max-width: 768px) {
          .feedback-form {
            padding: 1.5rem;
            margin: 1rem 0;
          }

          .rating-container {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .btn-submit {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default ArticleFeedbackForm; 